import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { AppModules } from '@common-modules/shared/app-modules.enum';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { WtrIconSize } from '@common-modules/shared/icon/icon.component';
import { DateRange } from '@common-modules/shared/model/date/date-range';
import { WtrColors } from '@common-modules/shared/styles/wtr-colors';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MapDateRange } from '../../models/map-date-range';
import { MapTimeStepSliderComponent } from '../map-time-step-slider/map-time-step-slider.component';

const COMPONENT_SELECTOR = 'wlm-map-time-bar';

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './map-time-bar.component.html',
  styleUrls: ['./map-time-bar.component.scss'],
  standalone: false,
})
export class MapTimeBarComponent implements OnInit {
  private _map: any;
  public get map(): any {
    return this._map;
  }
  @Input() public set map(value: any) {
    this._map = value;
    this.propagateStepChanged();
  }

  mapDateRange: MapDateRange;
  private _initialMapDateRange: MapDateRange;
  private _timeStepToIndex;
  @Input() set initialMapDateRange(value: MapDateRange) {
    this.mapDateRange = value;
    this._initialMapDateRange = globalUtilsHelper.clone(this.mapDateRange, true);

    this.hasAnyStep = Boolean(this.mapDateRange?.steps?.length);
    this.currentStepIndex = 0;
    this.setCurrentFromIndex();
    if (this.mapDateRange) {
      this.filterDateRange = new DateRange(this.mapDateRange.startDate, this.mapDateRange.endDate);
      this.buildMapDatesToIndex(this.mapDateRange);
    }
  }

  @Input() timeBarVelocity: number;

  @ViewChild(MapTimeStepSliderComponent) mapTimeStepSlider: MapTimeStepSliderComponent;
  @ViewChild(MatMenuTrigger) filterMenuTrigger: MatMenuTrigger;

  @HostListener('dblclick', ['$event']) onDoubleClick(event) {
    event.stopPropagation();
  }

  @Output() stepChange = new EventEmitter<Date>();
  @Output() dateRangeChange = new EventEmitter<DateRange>();

  private _currentStep: Date;
  public get currentStep(): Date {
    return this._currentStep;
  }
  public set currentStep(value: Date) {
    this._currentStep = value;
    this.propagateStepChanged();
  }

  private _currentDateRange: DateRange;
  public get currentDateRange(): DateRange {
    return this._currentDateRange;
  }
  private set currentDateRange(value: DateRange) {
    this._currentDateRange = value;
    this.propagateCurrentDateRangeChanged();
  }

  currentStepIndex: number;
  hasAnyStep: boolean;
  filterForm: FormGroup;
  maxDate = new Date();
  filterDateRange: DateRange;
  filterErrors: ValidationErrors;
  hasAnyFilterError: boolean;
  readonly iconSize: WtrIconSize = 'xs';
  playing = false;
  private readonly _playSpeed = 2000;
  private _stop$ = new Subject<void>();
  private _selectedDateRange: DateRange;
  readonly iconColor = WtrColors.NeutralColorWhite;
  readonly filterDebounceTime = 0;

  private _previousStepClick$ = new Subject<void>();
  previousStepClick$ = this._previousStepClick$.asObservable();
  private _nextStepClick$ = new Subject<void>();
  nextStepClick$ = this._nextStepClick$.asObservable();

  private _canAdvanceStep = true;
  private _pendingStepChange = false;
  private _pendingStepIndex: number;
  private _stepTimeout: any = null;
  private _currentStepIndex: number = 0;
  readonly T_SCOPE = `${AppModules.Map}.${COMPONENT_SELECTOR}`;

  constructor(private _fb: FormBuilder, private _element: ElementRef) {}

  ngOnInit(): void {
    this.filterForm = this._fb.group({
      startDate: [null, []],
      endDate: [null, []],
    });

    this.nextStepClick$
      .pipe(untilDestroyed(this), debounceTime(100))
      .subscribe(() => this.onNextStep());
    this.previousStepClick$
      .pipe(untilDestroyed(this), debounceTime(100))
      .subscribe(() => this.onPreviousStep());
  }

  public onMapLoadingStateChanged(isLoading: boolean): void {
    this._canAdvanceStep = !isLoading;

    if (!isLoading && this._pendingStepChange) {
      this._pendingStepChange = false;
      this.currentStepIndex = this._pendingStepIndex;
      this._currentStepIndex = this._pendingStepIndex;
      this.setCurrentFromIndex();
    }

    if (this._canAdvanceStep && this.playing) {
      this.programNextStep();
    }
  }

  getNativeElement() {
    return this._element.nativeElement;
  }

  onPlay(): void {
    if (!this.playing) {
      this.playing = true;
      this._currentStepIndex = 0;
      this.programNextStep();
    }
  }
  private programNextStep(): void {
    if (!this.playing || !this._canAdvanceStep) {
      return;
    }

    if (this._stepTimeout) {
      clearTimeout(this._stepTimeout);
    }

    this._stepTimeout = setTimeout(() => {
      if (!this.playing) return;

      if (this._currentStepIndex >= this.mapDateRange.steps.length) {
        this.onStop();
        return;
      }

      this.requestStepChange(this._currentStepIndex);
      this._currentStepIndex++;

      if (this._canAdvanceStep) {
        this.programNextStep();
      }
    }, this.timeBarVelocity ?? this._playSpeed);
  }

  private requestStepChange(stepIndex: number): void {
    if (!this._canAdvanceStep) {
      this._pendingStepChange = true;
      this._pendingStepIndex = stepIndex;
      return;
    }

    this.currentStepIndex = stepIndex;
    this._currentStepIndex = stepIndex;
    this.processNextStep();
  }

  private processNextStep(): void {
    this.setCurrentFromIndex();
  }

  private buildMapDatesToIndex(mapDateRange: MapDateRange){
    this._timeStepToIndex = mapDateRange.steps.reduce<Record<string,number>>((acc, timeStep, index) => {
      acc[timeStep.toISOString()] = index;
      return acc;
    }, {});
  }

  private setCurrentFromIndex(): void {
    this.currentStep = this.mapDateRange?.steps?.length
      ? this.mapDateRange.steps[this.currentStepIndex]
      : null;
  }

  onStop(): void {
    this.playing = false;
    if (this._stepTimeout) {
      clearTimeout(this._stepTimeout);
      this._stepTimeout = null;
    }
    this._stop$.next();
  }

  onPreviousStep(): void {
    if (this.currentStepIndex === 0) {
      this.currentStepIndex = this.mapDateRange.steps.length - 1;
    } else {
      this.currentStepIndex--;
    }

    if (!this._canAdvanceStep) {
      this._pendingStepChange = true;
      this._pendingStepIndex = this.currentStepIndex;
      return;
    }

    this.setCurrentFromIndex();
  }

  onPreviousStepClick(): void {
    this._previousStepClick$.next();
  }

  onNextStep(): void {
    if (this.currentStepIndex === this.mapDateRange.steps.length - 1) {
      this.currentStepIndex = 0;
    } else {
      this.currentStepIndex++;
    }
    if (!this._canAdvanceStep) {
      this._pendingStepChange = true;
      this._pendingStepIndex = this.currentStepIndex;

      return;
    }

    this.setCurrentFromIndex();
  }
  onNextStepClick(): void {
    this._nextStepClick$.next();
  }

  /**
   * Although the step is changed in this component and in step-time-bar,
   * we notify only in this flow to avoid duplicated events.
   */
  onStepChange(step: Date): void {
    this.currentStep = step;
    this.currentStepIndex = this._timeStepToIndex[step.toISOString()];
  }

  propagateStepChanged() {
    if (this._canAdvanceStep && this.map?.waterTimeBar) {
      this.map.waterTimeBar.setCurrentStep(this.currentStep);
      this.stepChange.emit(this.currentStep);
    }
  }

  propagateCurrentDateRangeChanged() {
    this.dateRangeChange.emit(this.currentDateRange);
  }
  onDateRangeChanged(range: DateRange): void {
    this._selectedDateRange = range;

    const dateRange = this._selectedDateRange ?? this.filterDateRange;
    const updatedDateRange = new MapDateRange();
    updatedDateRange.startDate = dateRange.start;
    updatedDateRange.endDate = dateRange.end;

    const startDateNumber = +new Date(updatedDateRange.startDate);
    let endDateNumber;
    if (updatedDateRange.endDate) {
      endDateNumber = +new Date(updatedDateRange.endDate);
    }

    const filteredSteps = this._initialMapDateRange.steps
      .map((step) => new Date(step))
      .filter((step) => {
        return startDateNumber <= +step && (endDateNumber ? +step <= endDateNumber : true);
      });
    updatedDateRange.steps = filteredSteps;
    this.currentStepIndex = 0;
    this.currentDateRange = range;
    this.mapDateRange = updatedDateRange;
    this.buildMapDatesToIndex(updatedDateRange);
    this.setCurrentFromIndex();
    this.filterMenuTrigger?.closeMenu();
  }

  onApplyDatesFilter(): void {}

  onDateRangeErrors(errors: ValidationErrors): void {
    this.filterErrors = errors;
    if (errors === null || typeof errors === 'undefined') {
      this.hasAnyFilterError = false;
    } else {
      this.hasAnyFilterError = Object.keys(errors).length !== 0;
    }
  }

  stopPropagation(event): void {
    event.stopPropagation();
  }
}
